import React, {
  MouseEvent,
  useState,
} from 'react';
import {
  Row,
  Table,
} from '@tanstack/react-table';
import {
  PatientAuthorizationAllowanceBasis,
} from 'dtos/patientAuthorizationAllowanceBasis';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  ExpandAllButton,
  ExpandRowButton,
} from 'pages/Dashboard/pages/Billing/Charges/columns/ExpandToggle';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'core/icons/delete';
import EditIcon from 'core/icons/edit';
import EditNoteIcon from 'core/icons/editNote';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import isEmpty from 'lodash/isEmpty';
import {
  isEmptyRichText,
} from 'views/EMR/Settings/helper';
import AuthorizationNotes from 'views/EMR/components/Authorizations/Notes';
import SanitizedHtml from 'views/EMR/components/SanitizedHtml';
import ToolTip from 'core/components/Tooltip';
import Name from 'pages/Dashboard/components/Name';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  formatDate,
  formatDateTime,
} from 'utils/date';
import AuthorizationStatusBadge from 'views/EMR/components/Authorizations/StatusBadge';
import AuthorizationNumber from 'views/EMR/components/Authorizations/AuthorizationNumber';
import {
  getAuthorizationQuantityTotals,
} from 'views/EMR/components/Authorizations/utils';
import type {
  AuthorizationRow,
} from 'views/EMR/components/Authorizations/utils';

type RowProps = {
  row: Row<AuthorizationRow>;
};

export type AuthorizationTableMeta = {
  onEdit: (authorizationId: number) => void;
  onDelete: (authorizationId: number, authorizationNumber: string) => void;
  isDeletePending: boolean;
};

function StatusCell({ row }: RowProps) {
  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <div className="flex h-full w-full items-center">
      <AuthorizationStatusBadge status={row.original.status} />
    </div>
  );
}

function InsuranceCell({ row }: RowProps) {
  const { payer } = row.original;
  const compactInfo = [payer.payerId, payer.insuranceType].filter(Boolean).join(' · ');
  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <div className="flex w-full min-w-0 flex-col gap-0.5">
      <span className="truncate font-medium text-gray-800">
        {payer.payerName}
      </span>
      <span className="truncate text-xs text-gray-500">
        {compactInfo}
      </span>
    </div>
  );
}

function ExpansionCell({ row }: RowProps) {
  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <ExpandRowButton
      expandable={row.getCanExpand()}
      isExpanded={row.getIsExpanded()}
      onToggle={(event) => {
        event.stopPropagation();
        row.toggleExpanded();
      }}
    />
  );
}

function AuthorizationCodeCell({ row }: RowProps) {
  const subrow = row.original.authorizationUnitSubrow;
  return subrow ? (
    <ToolTip
      title={subrow.description}
      placement="top-start"
      className="whitespace-normal break-words"
      popperClassName="code-description-tooltip"
      classes={{ tooltip: 'max-h-[300px] overflow-auto' }}
      isInteractive
    >
      <span className="font-medium text-gray-700">{subrow.code}</span>
    </ToolTip>
  ) : (
    <div className="flex w-full min-w-0 items-center text-left">
      <AuthorizationNumber
        authorizationNumber={row.original.authorizationNumber}
        className="text-gray-800"
      />
    </div>
  );
}

type QuantityTotalsCellProps = {
  allowances: AuthorizationRow['allowances'];
};

function QuantityTotalsCell({ allowances }: QuantityTotalsCellProps) {
  const totals = getAuthorizationQuantityTotals(allowances);
  return totals.length === 0 ? <span>{DEFAULT_EMPTY_VALUE}</span> : (
    <div className="flex w-full flex-col items-start gap-1 text-left text-xs">
      {totals.map((total) => {
        const basis = total.allowanceBasis === PatientAuthorizationAllowanceBasis.Units
          ? 'units'
          : 'visits';
        return (
          <div key={total.allowanceBasis}>
            <span className="text-gray-800">{total.usedQuantity}</span>
            <span className="text-gray-500"> of {total.approvedQuantity} {basis} used</span>
          </div>
        );
      })}
    </div>
  );
}

function QuantityCell({ row }: RowProps) {
  const subrow = row.original.authorizationUnitSubrow;
  return subrow ? (
    <div className="flex w-full flex-col items-start text-left text-xs">
      <span><span className="text-gray-800">{subrow.usedQuantity}</span><span className="text-gray-500"> of {subrow.approvedQuantity} units used</span></span>
    </div>
  ) : <QuantityTotalsCell allowances={row.original.allowances} />;
}

function UpdatedCell({ row }: RowProps) {
  const { updatedByUserId, updatedByUserName, updatedOn } = row.original;
  const displayName = updatedByUserName?.fullNameWithCredentials
    ?? updatedByUserName?.fullName
    ?? '';
  const imageSrc = useUserProfilePicturePath(updatedByUserId ?? undefined);
  return !displayName && !updatedByUserId ? <span>{DEFAULT_EMPTY_VALUE}</span> : (
    <Name
      {...updatedByUserName}
      name={displayName || `User ${updatedByUserId}`}
      image={{ size: 'sm', src: imageSrc }}
      classes={{
        root: '!min-w-0 !gap-2 !font-normal',
        nameRoot: '!min-w-0',
        label: '!text-xs !font-medium !leading-4 !text-gray-900',
      }}
    >
      <span className="truncate text-xs font-normal leading-4 text-gray-500">
        {formatDateTime(updatedOn)}
      </span>
    </Name>
  );
}

function EffectiveDateRangeCell({ row }: RowProps) {
  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <div className="w-full text-left font-semibold">
      <span className="text-gray-500">
        {[
          formatDate(row.original.effectiveDate),
          formatDate(row.original.expirationDate),
        ].join(' to ')}
      </span>
    </div>
  );
}

type EditCellProps = RowProps & {
  table: Table<AuthorizationRow>;
};

function EditCell({ row, table }: EditCellProps) {
  const { onEdit } = table.options.meta as AuthorizationTableMeta;
  const onEditClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const authorizationId = row.original.patientAuthorizationId;
    onEdit(authorizationId);
  };

  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <ToolTip title="Edit">
      <IconButton
        aria-label="Edit"
        size="small"
        onClick={onEditClick}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <EditIcon className="!h-5 !w-5 !text-black" />
      </IconButton>
    </ToolTip>
  );
}

type DeleteProps = RowProps & {
  table: Table<AuthorizationRow>;
};

function DeleteCell({ row, table }: DeleteProps) {
  const {
    onDelete: requestDelete,
    isDeletePending,
  } = table.options.meta as AuthorizationTableMeta;

  const onDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    requestDelete(
      row.original.patientAuthorizationId,
      row.original.authorizationNumber,
    );
  };

  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <ToolTip title="Delete">
      <IconButton
        aria-label="Delete"
        color="error"
        size="small"
        className="disabled:opacity-50"
        disabled={isDeletePending}
        onClick={onDelete}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <DeleteIcon className="!h-5 !w-5 !text-black" />
      </IconButton>
    </ToolTip>
  );
}

function NotesCell({ row }: RowProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const notes = row.original.notes ?? '';
  const plainText = notes.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|\u00a0/gi, ' ').trim();
  const isNotesEmpty = isEmpty(notes) || isEmptyRichText(notes) || !plainText;
  const Icon = isNotesEmpty ? NotesRoundedIcon : EditNoteIcon;
  return row.original.authorizationUnitSubrow !== undefined ? null : (
    <>
      <ToolTip
        title={isNotesEmpty
          ? 'Add notes'
          : (
            <div className="flex max-w-[250px] flex-col gap-2">
              <strong>Authorization Notes</strong>
              <SanitizedHtml content={notes} />
            </div>
          )}
        placement='top'
        isInteractive
      >
        <IconButton
          size="small"
          aria-label="Notes"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Icon className="!h-5 !w-5 !text-black" />
        </IconButton>
      </ToolTip>
      {anchorEl && (
        <AuthorizationNotes
          authorizationId={row.original.patientAuthorizationId}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
        />
      )}
    </>
  );
}

const authorizationColumns: ColumnDef<AuthorizationRow>[] = [{
    id: 'expand',
    header: '',
    columnTitle: (table: Table<AuthorizationRow>) => <ExpandAllButton table={table} />,
    size: 48,
    minSize: 48,
    cell: ({ row }) => <ExpansionCell row={row} />,
  }, {
    id: 'status',
    header: 'Status',
    size: 150,
    minSize: 150,
    cell: ({ row }) => <StatusCell row={row} />,
  }, {
    id: 'insurance',
    header: 'Insurance',
    size: 220,
    minSize: 180,
    meta: { autoSized: true },
    cell: ({ row }) => <InsuranceCell row={row} />,
  }, {
    id: 'authorizationNumber',
    accessorKey: 'authorizationNumber',
    header: 'Authorization #',
    size: 190,
    minSize: 160,
    meta: { autoSized: true },
    cell: ({ row }) => <AuthorizationCodeCell row={row} />,
  }, {
    id: 'quantity',
    header: 'Total used',
    size: 150,
    minSize: 130,
    cell: ({ row }) => <QuantityCell row={row} />,
  }, {
    id: 'effectiveDateRange',
    header: 'Effective',
    size: 180,
    minSize: 160,
    meta: { autoSized: true },
    cell: ({ row }) => <EffectiveDateRangeCell row={row} />,
  }, {
    id: 'updated',
    accessorKey: 'updatedOn',
    header: 'Updated',
    size: 200,
    minSize: 200,
    cell: ({ row }) => row.original.authorizationUnitSubrow === undefined
      ? <UpdatedCell row={row} />
      : null,
  }, {
    id: 'actions',
    header: '',
    size: 120,
    minSize: 120,
    cell: ({ row, table }) => (
      <div className="flex items-center">
        <NotesCell row={row} />
        <EditCell row={row} table={table} />
        <DeleteCell row={row} table={table} />
      </div>
    ),
  }];

export default authorizationColumns;
